import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Screen } from '../../components/lease/Screen'
import { BrandHeading } from '../../components/lease/BrandHeading'
import { PrimaryButton } from '../../components/lease/PrimaryButton'
import { BoardBadge } from '../../components/lease/BoardBadge'
import { attorneysApi } from '../../services/attorneys'

// Mockup "12 Local Attorneys": browse the referral directory, sponsored
// profiles first (FTC-labeled), pick one or let LeaseCheck pick a sponsor.
function Initials({ name }) {
  const initials = name
    .split(' ')
    .map((p) => p[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()
  return (
    <span className="flex h-12 w-12 flex-none items-center justify-center rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 text-sm font-bold text-white">
      {initials}
    </span>
  )
}

function AttorneyCard({ attorney, selected, onSelect }) {
  return (
    <button
      type="button"
      onClick={() => onSelect(attorney.id)}
      className={`w-full rounded-2xl bg-white/70 p-4 text-left shadow-sm ring-1 backdrop-blur transition dark:bg-slate-800/60 ${
        selected ? 'ring-2 ring-emerald-500 dark:ring-emerald-400' : 'ring-emerald-100 dark:ring-slate-700'
      }`}
    >
      <div className="flex items-center gap-3">
        {attorney.photo_url ? (
          <img src={attorney.photo_url} alt="" className="h-12 w-12 flex-none rounded-full object-cover" />
        ) : (
          <Initials name={attorney.name} />
        )}
        <div>
          <div className="flex items-center gap-2">
            <span className="font-semibold text-slate-800 dark:text-slate-100">{attorney.name}</span>
            {attorney.sponsored && (
              <span className="rounded-full bg-gradient-to-r from-emerald-600 to-teal-600 px-2 py-0.5 text-xs font-semibold text-white">
                Sponsored
              </span>
            )}
          </div>
          <p className="text-sm text-slate-600 dark:text-slate-300">{attorney.firm}</p>
          {attorney.board_member && (
            <span className="mt-1 inline-block">
              <BoardBadge tier={attorney.board_tier} member />
            </span>
          )}
        </div>
      </div>
      {attorney.bio && (
        <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">{attorney.bio}</p>
      )}
    </button>
  )
}

export function LocalAttorneys() {
  const navigate = useNavigate()
  const [attorneys, setAttorneys] = useState(null)
  const [error, setError] = useState(null)
  const [selectedId, setSelectedId] = useState(null)
  const [pickForMe, setPickForMe] = useState(false)
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' })
  const [sending, setSending] = useState(false)
  const [sendError, setSendError] = useState(null)
  const [sentTo, setSentTo] = useState(null)

  useEffect(() => {
    attorneysApi.list().then(setAttorneys).catch((e) => setError(e.message))
  }, [])

  const handleSelect = (id) => {
    setSelectedId(id)
    setPickForMe(false)
  }

  const handlePickForMe = () => {
    setPickForMe(true)
    setSelectedId(null)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSending(true)
    setSendError(null)
    try {
      const result = await attorneysApi.submitInquiry({
        ...form,
        ...(pickForMe ? { pick_for_me: true } : { attorney_id: selectedId }),
      })
      setSentTo(result.attorney)
    } catch (err) {
      setSendError(err.message)
    } finally {
      setSending(false)
    }
  }

  if (sentTo) {
    return (
      <Screen className="text-center">
        <header className="animate-fade-in">
          <BrandHeading className="text-4xl">Message sent!</BrandHeading>
          <p className="mt-4 text-slate-700 dark:text-slate-200">
            Your message is on its way to {sentTo.name} at {sentTo.firm}.
          </p>
        </header>
        <PrimaryButton onClick={() => navigate(-1)} className="mt-8 animate-slide-in">
          Back to report
        </PrimaryButton>
      </Screen>
    )
  }

  if (error) {
    return (
      <Screen className="text-center">
        <p className="text-slate-600 dark:text-slate-300">Could not load attorneys. Try again shortly.</p>
      </Screen>
    )
  }

  if (!attorneys) {
    return (
      <Screen className="text-center">
        <p className="text-slate-600 dark:text-slate-300">Loading attorneys…</p>
      </Screen>
    )
  }

  const sponsored = attorneys.filter((a) => a.sponsored)
  const regular = attorneys.filter((a) => !a.sponsored)
  const canSubmit = (selectedId || pickForMe) && form.name && form.email

  return (
    <Screen>
      <header className="animate-fade-in text-center">
        <BrandHeading className="text-4xl">Local Attorneys</BrandHeading>
        <p className="mt-3 text-slate-600 dark:text-slate-300">
          Connect with a Colorado attorney to review your lease, or let us pick one for you.
        </p>
      </header>

      <PrimaryButton onClick={handlePickForMe} className="mt-6 animate-slide-in">
        Pick one for me
      </PrimaryButton>
      {pickForMe && (
        <p className="mt-2 text-center text-sm font-semibold text-emerald-700 dark:text-emerald-400">
          We'll route your message to one of our sponsored attorneys.
        </p>
      )}

      {sponsored.length > 0 && (
        <section className="mt-8 animate-slide-in">
          <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100">Sponsored Profiles</h2>
          <div className="mt-4 space-y-3">
            {sponsored.map((a) => (
              <AttorneyCard key={a.id} attorney={a} selected={selectedId === a.id} onSelect={handleSelect} />
            ))}
          </div>
        </section>
      )}

      {regular.length > 0 && (
        <section className="mt-8 animate-slide-in">
          <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100">Local Attorneys</h2>
          <div className="mt-4 space-y-3">
            {regular.map((a) => (
              <AttorneyCard key={a.id} attorney={a} selected={selectedId === a.id} onSelect={handleSelect} />
            ))}
          </div>
        </section>
      )}

      <section className="mt-8 animate-slide-in">
        <h2 className="text-2xl font-bold text-slate-800 dark:text-slate-100">Submit Your Inquiry</h2>
        <form onSubmit={handleSubmit} className="mt-4 space-y-3">
          <input
            type="text"
            placeholder="Your Name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="w-full rounded-xl border-0 bg-white/70 p-3 text-slate-800 shadow-sm ring-1 ring-emerald-100 backdrop-blur placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:bg-slate-800/60 dark:text-slate-100 dark:ring-slate-700"
          />
          <input
            type="email"
            placeholder="Your Email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="w-full rounded-xl border-0 bg-white/70 p-3 text-slate-800 shadow-sm ring-1 ring-emerald-100 backdrop-blur placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:bg-slate-800/60 dark:text-slate-100 dark:ring-slate-700"
          />
          <input
            type="tel"
            placeholder="Your Phone"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            className="w-full rounded-xl border-0 bg-white/70 p-3 text-slate-800 shadow-sm ring-1 ring-emerald-100 backdrop-blur placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:bg-slate-800/60 dark:text-slate-100 dark:ring-slate-700"
          />
          <textarea
            placeholder="Your Message"
            value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })}
            rows={4}
            className="w-full rounded-xl border-0 bg-white/70 p-3 text-slate-800 shadow-sm ring-1 ring-emerald-100 backdrop-blur placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:bg-slate-800/60 dark:text-slate-100 dark:ring-slate-700"
          />

          {sendError && (
            <p className="text-center text-sm text-rose-600 dark:text-rose-400">{sendError}</p>
          )}

          <PrimaryButton type="submit" disabled={!canSubmit || sending}>
            {sending ? 'Sending…' : 'Send'}
          </PrimaryButton>
        </form>
      </section>

      <footer className="mt-6 text-center text-xs text-slate-500 dark:text-slate-400">
        This directory is provided for convenience. LeaseCheck does not endorse any
        attorney's services. Sponsored listings receive priority placement.
      </footer>
    </Screen>
  )
}

export default LocalAttorneys
